import React, { Suspense } from "react";

import {
	faCalendarMinus,
	faEllipsisV,
	faPen,
	faTimes,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import cx from "classnames";
import { motion } from "framer-motion";
import { graphql } from "relay-runtime";

import { MeetingCostViewPanel } from "~/calendar/MeetingGuidelinesPrompts/";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Tooltip } from "~/ui";
import IconButton from "~/ui/legacy/IconButton";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";

import type { Event, MeetingCostSettings } from "../CalendarPage";
import useDeleteEvent from "../hooks/useDeleteEvent";
import useEventVisibilityToggle from "../hooks/useEventVisibilityToggle";
import { SHARP_EASE_OUT_CUBIC_BEZIER } from "../SidePanel";
import { ViewOptionsMenuTrigger } from "../Toolbar/ViewOptionsMenu";
import isEventEditable from "../utils/isEventEditable";
import AssignedActionItemsRow from "./AssignedActionItemsRow";
import AttachmentsRow from "./AttachmentsRow";
import AttendeesRow from "./AttendeesRow";
import Banners from "./Banners";
import CalendarRow from "./CalendarRow";
import CollapseButton from "./CollapseButton";
import DescriptionRow from "./DescriptionRow";
import EventTimeRow from "./EventTimeRow";
import ExpandButton from "./ExpandButton";
import JoinConferenceRow from "./JoinConferenceRow";
import LocationRow from "./LocationRow";
import Note from "./Note";
import RecordingRow from "./RecordingRow";
import RSVPRow from "./RSVPRow";

import type { ViewEventDetailQuery } from "~/__generated__/ViewEventDetailQuery.graphql";

import panelStyles from "../SidePanel.module.css";
import styles from "./ViewEvent.module.css";

const eventInfoQuery = graphql`
	query ViewEventDetailQuery($guid: String) {
		viewer {
			remainingMeetingRecordingCredits
			account {
				id
				defaultMeetingTemplate {
					id
					proseDoc
					schemaVersion
				}

				defaultOneononeTemplate {
					id
					proseDoc
					schemaVersion
				}

				defaultBasicTemplate {
					id
					proseDoc
					schemaVersion
				}
			}

			...RecordingRow_viewer
		}

		event: eventByGuid(guid: $guid) {
			... on Event {
				id
				isOneOnOne
				description
				recurrenceRule
				userResponseStatus
				attendees {
					__typename
					email
					fullName
					image
					invitable
					title
					responseStatus
					isOptional
					isOrganizer
					user {
						id
						isTopUser
					}
				}
				conferenceCalls {
					provider
					url
					phoneNumber
					pin
				}
				attachments {
					provider
					fileId
					url
					fileType
					title
				}
				userTodos {
					done
				}
				status
				location
				calendar {
					id
					source
					name
					color
					sourcePrimary
				}
				requiredSectionsCount
				stream {
					id
					carryActionItems
					carryTalkingPoints
					schedulingMeetingAssistantAutomationIsActive
					template {
						id
						proseDoc
						schemaVersion
					}
				}
				previousOccurrence {
					note {
						pendingTodoCount
						pendingAgendaCount
					}
				}
				note {
					id
					title
					url
					eventStart
					updatedAt
					createdAt
					assignedTodoCount
					rawDraftJson
					noteuserSet {
						id
						lastSeenAt
						user {
							__typename
							id
							type
							email
							firstName
							fullName
							image
							isActive
							isConfigured
							isGuest
							isTopUser
							isExternal

							internalAttributes {
								invitable
							}
						}
					}
					externalUsers {
						__typename
						id
						type
						email
						firstName
						fullName
						image
						isActive
						isConfigured
						isGuest
						isTopUser
						isExternal

						internalAttributes {
							invitable
						}
					}
					attachments {
						id
						filename
						filetype
						size
						updatedAt
						url
						dimensions {
							width
							height
						}
					}
					meetingRecordings {
						id
						meetingCallStart
						meetingRecordedVideoDuration
						meetingRecordedVideoPosterUrl
						...utils_useLongestMeetingRecording_meetingRecordings
						...utils_AddedOrAvailableUntil_meetingRecording
					}
				}
				nextOccurrence {
					...RecordIcon_event @relay(mask: false)
				}
			}
		}
	}
`;

export type EventExtraData = NonNullable<ViewEventDetailQuery["response"]["event"]>;
export type RowProps = { event: Event; eventData: EventExtraData };

type Props = {
	event: Event;
	onClose: () => void;
	panelWasOpen: boolean;
	onEditClicked: () => void;
	fetchKey: number;
	meetingCostSettings: MeetingCostSettings;
	hasAccessToSchedulingMeetingAssistant: boolean;
};

function ViewEventInner({
	event,
	onClose,
	onEditClicked,
	fetchKey,
	meetingCostSettings,
	hasAccessToSchedulingMeetingAssistant,
}: Props) {
	const [expanded, setExpanded] = React.useState(false);
	const [shouldShowRsvp, setShouldShowRsvp] = React.useState(false);
	const expandClicked = React.useCallback(() => {
		setExpanded(true);
		dispatchAnalyticsTrack("View event action", { feature: "Calendar", action: "expand" });
	}, []);
	const collapseClicked = React.useCallback(() => {
		setExpanded(false);
		dispatchAnalyticsTrack("View event action", { feature: "Calendar", action: "collapse" });
	}, []);
	React.useEffect(() => {
		setExpanded(false);
		setShouldShowRsvp(false);
	}, [event.guid]);

	const onAnsweredRsvp = React.useCallback(() => {
		setShouldShowRsvp(true);
	}, []);

	const { canEdit, reason: editPreventionReason } = React.useMemo(() => isEventEditable(event), [event]);

	const onHideEvent = useEventVisibilityToggle(event, {
		onHide: (
			<>
				<p style={{ pointerEvents: "none" }}>
					You can reveal hidden events by navigating to the <ViewOptionsMenuTrigger isOpen={false} /> menu at the top of
					the calendar page and selecting <i>View settings → Hidden meetings</i>.
				</p>
				<p>Hiding a recurring event will also hide all events in the series.</p>
			</>
		),
		onReveal: "Showing a recurring event will also show all events in the series.",
	});

	const onDeleteEvent = useDeleteEvent(event);

	const data = useLazyLoadQuery<ViewEventDetailQuery>(
		eventInfoQuery,
		{ guid: event.guid },
		{ fetchPolicy: "store-and-network", fetchKey },
	);
	const eventData = data.event;
	if (!eventData) {
		return null;
	}

	const needRsvp = ["notResponded", "needsAction"].includes(eventData.userResponseStatus || "");
	const isTimeBlock = event.timeBlockType === "focusTime" || event.timeBlockType === "outOfOffice";

	return (
		<div>
			<div className={panelStyles.sidePanel__header}>
				<div className={panelStyles.sidePanel__header__title} title={event.title}>
					{event.title}
				</div>

				<div className={panelStyles.buttons}>
					<Tooltip content={editPreventionReason} placement="bottom" disabled={canEdit || !editPreventionReason}>
						<span>
							<IconButton title="Edit" icon={faPen} onClick={onEditClicked} disabled={!canEdit} />
						</span>
					</Tooltip>
					<Menu>
						<Menu.Trigger>
							<IconButton title="Menu" icon={faEllipsisV} />
						</Menu.Trigger>
						<Menu.Content align="end">
							<Menu.Item icon={faCalendarMinus} onSelect={onHideEvent}>
								{event.isHidden ? "Show" : "Hide"} event in Fellow
							</Menu.Item>
							{canEdit && (
								<Menu.Item destructive icon={faTrashCan} onSelect={onDeleteEvent}>
									Delete event
								</Menu.Item>
							)}
						</Menu.Content>
					</Menu>
					<IconButton title="Close" icon={faTimes} onClick={onClose} />
				</div>
			</div>

			<div className={panelStyles.sidePanel__body}>
				<div className={styles.heading}>
					{expanded ? (
						<>
							<EventTimeRow event={event} eventData={eventData} />
							<JoinConferenceRow expanded={expanded} event={event} eventData={eventData} />
							{meetingCostSettings?.meetingCostEnabled && meetingCostSettings?.meetingCostEnforceWorkspaceSettings && (
								<Suspense fallback={<LoadingSpinner />}>
									<MeetingCostViewPanel
										attendees={eventData.attendees}
										event={event}
										recurrenceRule={eventData.recurrenceRule}
									/>
								</Suspense>
							)}
							<AttendeesRow event={event} eventData={eventData} />
							<AssignedActionItemsRow count={eventData.note?.assignedTodoCount} />
							<DescriptionRow event={event} eventData={eventData} />
							<AttachmentsRow attachments={eventData.attachments} />
							<CalendarRow calendar={eventData.calendar} />
							<LocationRow location={eventData.location} />
							<RecordingRow
								event={event}
								eventData={eventData}
								hasAccessToSchedulingMeetingAssistant={hasAccessToSchedulingMeetingAssistant}
								viewer={data.viewer}
							/>
							{!isTimeBlock && <RSVPRow event={event} eventData={eventData} />}
							<CollapseButton collapseClicked={collapseClicked} />
						</>
					) : (
						<>
							<EventTimeRow event={event} eventData={eventData} />
							<JoinConferenceRow expanded={expanded} event={event} eventData={eventData} />
							<RecordingRow
								event={event}
								eventData={eventData}
								hasAccessToSchedulingMeetingAssistant={hasAccessToSchedulingMeetingAssistant}
								viewer={data.viewer}
							/>
							{(needRsvp || shouldShowRsvp) && !isTimeBlock && (
								<RSVPRow event={event} eventData={eventData} onAnsweredRsvp={onAnsweredRsvp} />
							)}
							<ExpandButton expandClicked={expandClicked} />
						</>
					)}
				</div>

				<Banners event={event} eventData={eventData} editClicked={onEditClicked} />

				<Note key={event.guid} event={event} eventData={eventData} viewer={data.viewer} />
			</div>
		</div>
	);
}

const LoadingIcon = () => <div className={styles.iconLoader} />;

const InfoRowLoader = () => (
	<div className={styles.infoRow}>
		<div className={cx(styles.infoRow__icon, styles.iconLoaderContainer)}>
			<LoadingIcon />
		</div>
		<div className={styles.infoRow__text}>
			<div className={styles.infoRowLoader} />
		</div>
	</div>
);

function LoadingPage({ onClose }: { onClose: Props["onClose"] }) {
	return (
		<div>
			<div className={panelStyles.sidePanel__header}>
				<div className={panelStyles.sidePanel__header__title}>
					<div className={styles.headerLoader} />
				</div>

				<div className={panelStyles.buttons}>
					<LoadingIcon />
					<LoadingIcon />
					<IconButton title="Close" icon={faTimes} onClick={onClose} />
				</div>
			</div>
			<div className={panelStyles.sidePanel__body}>
				<div className={styles.heading}>
					<InfoRowLoader />
					<InfoRowLoader />
				</div>
				<div className={styles.noteLoader} />
			</div>
		</div>
	);
}

export default function ViewEventWrapper(props: Props) {
	return (
		<motion.div
			className={styles.levelOnePanel}
			key="event-info"
			initial={props.panelWasOpen ? { left: "100%" } : false}
			transition={{ duration: 0.3, ease: SHARP_EASE_OUT_CUBIC_BEZIER }}
			animate={{ left: "0%" }}
			exit={{ left: "100%" }}
		>
			<React.Suspense fallback={<LoadingPage onClose={props.onClose} />}>
				<ViewEventInner {...props} />
			</React.Suspense>
		</motion.div>
	);
}
